import { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import { doc, getDoc, getDocs, onSnapshot, collection, query, where } from "firebase/firestore";

function chunk(array, size) {
  const out = [];
  for (let i = 0; i < array.length; i += size) out.push(array.slice(i, i + size));
  return out;
}

// Carica lezioni (eventi), presenze, quiz indipendenti (senza lezione associata) e risultati
// quiz di un corso, ricalcolando quando cambia l'elenco eventiIds. Quiz indipendenti e risultati
// quiz restano in ascolto realtime. I quiz indipendenti vengono esposti come "lezioni virtuali"
// (stesso shape {id, titolo, dataInizio}, con standalone:true) così che il calcolo dei KPI possa
// trattarli allo stesso modo delle lezioni vere ai fini di quiz/trend, escludendoli invece dal
// calcolo di presenza/costanza (che richiede una lezione reale).
export function useKpiCorsoData(corsoId, eventiIds) {
  const [eventi, setEventi] = useState([]);
  const [presenze, setPresenze] = useState([]);
  const [risultatiQuiz, setRisultatiQuiz] = useState([]);
  const [quizIndipendenti, setQuizIndipendenti] = useState([]);
  const [loading, setLoading] = useState(true);

  const eventiIdsKey = (eventiIds || []).slice().sort().join(",");

  useEffect(() => {
    const ids = eventiIds || [];
    if (ids.length === 0) { setEventi([]); setPresenze([]); setLoading(false); return; }
    setLoading(true);
    (async () => {
      const eventiDocs = await Promise.all(ids.map((id) => getDoc(doc(db, "eventi", id))));
      setEventi(eventiDocs.filter((s) => s.exists()).map((s) => ({ id: s.id, ...s.data() })));

      const gruppi = chunk(ids, 10);
      const risultati = await Promise.all(gruppi.map((g) =>
        getDocs(query(collection(db, "reportPresenze"), where("eventoId", "in", g)))
      ));
      setPresenze(risultati.flatMap((snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
      setLoading(false);
    })();
    // eventiIdsKey è la versione stabile (ordinata, in stringa) di eventiIds: rieseguiamo solo
    // quando il suo CONTENUTO cambia, non ad ogni nuovo riferimento d'array del genitore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventiIdsKey]);

  useEffect(() => {
    if (!corsoId) return;
    const q = query(collection(db, "risultatiQuizLezione"), where("corsoId", "==", corsoId));
    const unsub = onSnapshot(q, (snap) => {
      setRisultatiQuiz(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [corsoId]);

  useEffect(() => {
    if (!corsoId) return;
    const q = query(collection(db, "quizIndipendenti"), where("corsoId", "==", corsoId));
    const unsub = onSnapshot(q, (snap) => {
      setQuizIndipendenti(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [corsoId]);

  const eventiConQuizIndipendenti = useMemo(() => [
    ...eventi,
    ...quizIndipendenti.map((q) => ({ id: q.id, titolo: q.nome, dataInizio: q.data, standalone: true })),
  ], [eventi, quizIndipendenti]);

  return { eventi: eventiConQuizIndipendenti, quizIndipendenti, presenze, risultatiQuiz, loading };
}
